import { useEffect, useMemo, useState } from 'react';
import { Copy, ExternalLink, ImageOff, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  formatBytes,
  formatFuzzyDate,
  personDisplayName,
  type MediaSimilarHit,
  type MediaSummary,
} from '@familienbaum/shared';
import { useResolveStagedUpload } from '@/lib/queries';
import { ApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';

/**
 * Ein Fund samt der Datei, aus der er entstanden ist. Die Datei kommt nicht vom
 * Server: sie liegt noch im Browser und lässt sich damit ohne Umweg als
 * Vorschau zeigen – während dasselbe Bild oben auf dem Server im Zwischenlager
 * auf die Entscheidung wartet.
 */
export interface PendingDuplicate {
  hit: MediaSimilarHit;
  file?: File;
}

/**
 * Wie deutlich der Fund ist. Die Grenzen folgen der Messreihe in
 * `apps/api/src/lib/image-hash.ts`: dasselbe Bild bei anderem Licht liegt bei
 * 0–3 Bit, eine zweite Aufnahme davon bei 5–8, zwei verschiedene Bilder erst
 * ab 25.
 */
function verdictFor(distance: number): { tone: 'danger' | 'warn' | 'info'; label: string; hint: string } {
  if (distance <= 2) {
    return {
      tone: 'danger',
      label: 'So gut wie sicher dasselbe Bild',
      hint: 'Die Fingerabdrücke sind praktisch gleich – vermutlich dieselbe Vorlage, nur neu gespeichert oder anders belichtet.',
    };
  }
  if (distance <= 8) {
    return {
      tone: 'warn',
      label: 'Sehr wahrscheinlich dasselbe Bild',
      hint: 'Typisch für dieselbe Vorlage, ein zweites Mal abfotografiert: anderes Licht, etwas anderer Ausschnitt.',
    };
  }
  return {
    tone: 'info',
    label: 'Womöglich dasselbe Bild',
    hint: 'Ähnlicher Aufbau. Das kann auch eine zweite Aufnahme derselben Situation sein – dann ist es ein eigenes Bild.',
  };
}

/** Eine Seite des Vergleichs. Beide Bilder gleich groß, damit man sie überlagern kann. */
function ComparePane({
  label,
  badge,
  src,
  href,
  alt,
  children,
}: {
  label: string;
  badge?: React.ReactNode;
  src: string | null;
  href?: string;
  alt: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold tracking-wide text-ink-3 uppercase">{label}</span>
        {badge}
      </div>

      {/*
        `object-contain` statt `cover`: ein Ausschnitt würde genau den Rand
        wegschneiden, an dem sich zwei Aufnahmen desselben Fotos unterscheiden.
      */}
      <div className="relative aspect-4/3 overflow-hidden rounded-lg border border-line bg-sunken">
        {src ? (
          <img src={src} alt={alt} className="size-full object-contain" />
        ) : (
          <span className="flex size-full flex-col items-center justify-center gap-1 text-ink-3">
            <ImageOff className="size-6" aria-hidden />
            <span className="text-[12px]">Keine Vorschau</span>
          </span>
        )}
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="absolute right-1.5 bottom-1.5 flex items-center gap-1 rounded-md bg-black/55 px-2 py-1 text-[11.5px] text-white backdrop-blur-[2px] hover:bg-black/70"
          >
            <ExternalLink className="size-3" aria-hidden />
            Groß ansehen
          </a>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-col gap-0.5 text-[12.5px] leading-5">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <p className="flex min-w-0 flex-col gap-0 sm:flex-row sm:gap-1.5">
      <span className="shrink-0 text-ink-3">{label}</span>
      <span className="min-w-0 wrap-anywhere text-ink-2">{value}</span>
    </p>
  );
}

/**
 * Rückfrage vor dem Doppelgänger.
 *
 * Kommt, wenn ein Upload einem vorhandenen Bild zum Verwechseln ähnlich sieht,
 * ohne dieselbe Datei zu sein – typisch für ein Foto, das ein zweites Mal
 * abfotografiert wurde, bei anderem Licht. Byteidentische Dateien landen hier
 * nicht: die überspringt der Server ohne Rückfrage.
 *
 * Entschieden wird einzeln und ausdrücklich. Es gibt bewusst keinen
 * hervorgehobenen Knopf: welches der beiden Bilder ins Archiv gehört, weiß nur
 * die Person, die es aufgenommen hat.
 */
export function DuplicateReviewDialog({
  pending,
  onOpenChange,
  onKept,
  onDiscarded,
}: {
  pending: PendingDuplicate[];
  onOpenChange: (open: boolean) => void;
  /** Wird für jeden „trotzdem hochladen"-Fall mit dem neuen Medium aufgerufen. */
  onKept?: (media: MediaSummary) => void;
  /**
   * Wird beim Verwerfen aufgerufen. Nützlich, wo der Upload an etwas hängt –
   * eine Quelle etwa nimmt dann das vorhandene Bild als Belegstück, statt ohne
   * Beleg dazustehen.
   */
  onDiscarded?: (hit: MediaSimilarHit) => void;
}) {
  const [index, setIndex] = useState(0);
  const resolve = useResolveStagedUpload();

  // Neuer Stapel: wieder vorn anfangen.
  useEffect(() => {
    setIndex(0);
  }, [pending]);

  const current = pending[index];

  /**
   * Die Blob-URL wird beim Wechsel wieder freigegeben. Ohne das hält der Browser
   * jedes angesehene Foto bis zum Neuladen der Seite im Speicher – bei einem
   * Stapel abfotografierter Bilder sind das schnell hundert Megabyte.
   */
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!current?.file) {
      setLocalUrl(null);
      return;
    }
    const url = URL.createObjectURL(current.file);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [current?.file]);

  const verdict = useMemo(() => verdictFor(current?.hit.distance ?? 64), [current?.hit.distance]);

  if (!current) return null;

  const { hit } = current;
  const existing = hit.existing;
  const existingSrc = existing.urls.medium ?? existing.urls.large ?? existing.urls.thumb ?? null;
  const remaining = pending.length - index - 1;

  const decide = async (keep: boolean) => {
    try {
      const result = await resolve.mutateAsync({ stagingId: hit.stagingId, keep });
      if (keep) {
        if (result) onKept?.(result);
        toast.success(`${hit.filename} ist im Archiv.`);
      } else {
        onDiscarded?.(hit);
        toast.info(`${hit.filename} wurde verworfen.`);
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Entscheidung fehlgeschlagen');
      return;
    }

    if (remaining > 0) setIndex((i) => i + 1);
    else onOpenChange(false);
  };

  /**
   * Weggeklickt heißt „später": der Server hält die Datei noch und räumt sie
   * erst nach einem Tag ab. Das wird gesagt, statt es zu verschweigen – sonst
   * wartet man auf ein Bild, das nie im Archiv ankommt.
   */
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      const undecided = pending.length - index;
      if (undecided > 0) {
        toast.info(
          undecided === 1
            ? 'Unentschieden – die Datei wird nach 24 Stunden verworfen.'
            : `${undecided} Dateien bleiben unentschieden und werden nach 24 Stunden verworfen.`,
        );
      }
    }
    onOpenChange(open);
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        size="lg"
        title="Ist das dasselbe Bild?"
        description={
          pending.length > 1
            ? `Rückfrage ${index + 1} von ${pending.length} – nur diese Datei betrifft es.`
            : 'Diese Datei ist neu, sieht aber einem Bild im Archiv zum Verwechseln ähnlich.'
        }
        footer={
          <>
            <Button
              variant="danger"
              loading={resolve.isPending && resolve.variables?.keep === false}
              disabled={resolve.isPending}
              onClick={() => void decide(false)}
            >
              <Trash2 aria-hidden />
              Verwerfen
            </Button>
            <Button
              variant="secondary"
              loading={resolve.isPending && resolve.variables?.keep === true}
              disabled={resolve.isPending}
              onClick={() => void decide(true)}
            >
              <Upload aria-hidden />
              Trotzdem hochladen
            </Button>
          </>
        }
      >
        {/*
          Reihenfolge nach Nutzen: erst die Einschätzung in einer Zeile, dann
          die Bilder, dann der Text. Auf einem Telefon entscheidet das darüber,
          ob man die beiden Bilder ohne Scrollen sieht – und gesehen werden
          müssen sie, gelesen werden muss hier nichts.
        */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={verdict.tone}>
              <Copy className="size-3" aria-hidden />
              {verdict.label}
            </Badge>
            <span className="text-[11.5px] text-ink-3">Unterschied: {hit.distance} von 64 Bit</span>
          </div>

          {/*
            Zwei Spalten auch auf dem Telefon. Untereinander müsste man zum
            Vergleichen scrollen, und dann vergleicht man nicht mehr, sondern
            erinnert sich – wobei genau das hier die Frage ist. Klein und
            nebeneinander schlägt groß und nacheinander; für die Einzelheiten
            gibt es „Groß ansehen".
          */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <ComparePane
              label="Im Archiv"
              src={existingSrc}
              href={existing.urls.original}
              alt={existing.title ?? existing.originalFilename}
              badge={
                existing.processingState === 'pending' ? (
                  <Badge tone="neutral">wird noch verarbeitet</Badge>
                ) : null
              }
            >
              <Row label="Datei" value={existing.title ?? existing.originalFilename} />
              <Row label="Aufgenommen" value={formatFuzzyDate(existing.takenAt)} />
              <Row label="Hinzugefügt" value={formatDate(existing.createdAt)} />
              <Row
                label="Personen"
                value={
                  existing.persons.length
                    ? existing.persons.map((person) => personDisplayName(person)).join(', ')
                    : 'noch keine markiert'
                }
              />
            </ComparePane>

            <ComparePane
              label="Neu"
              src={localUrl}
              href={localUrl ?? undefined}
              alt={hit.filename}
            >
              <Row label="Datei" value={hit.filename} />
              <Row label="Größe" value={formatBytes(hit.bytes)} />
            </ComparePane>
          </div>

          {/*
            Was auf dem Spiel steht, gehört sichtbar an den Dialog: „Verwerfen"
            klingt nach mehr, als es ist, und wer das nicht weiß, lädt lieber
            doppelt hoch.
          */}
          <div className="flex flex-col gap-1 rounded-lg border border-line bg-elevated px-3 py-2.5 text-[12px] leading-5">
            <p className="text-ink-2">{verdict.hint}</p>
            <p className="text-ink-3">
              <span className="font-medium text-ink-2">Verwerfen</span> löscht nur diesen Upload –
              das Bild im Archiv bleibt samt Markierungen, Personen und Angaben unberührt.{' '}
              <span className="font-medium text-ink-2">Trotzdem hochladen</span> legt es als eigenes
              Medium daneben.
              {remaining > 0
                ? ` Danach ${remaining === 1 ? 'folgt noch eine Rückfrage' : `folgen noch ${remaining} Rückfragen`}.`
                : ''}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
